import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, shareReplay, catchError, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { FileModel } from '../models/file.model';
import { PaginatedList } from '../models/shared/paginated-list.model';
import { FileSortBy, GetFilesRequest } from '../models/shared/file-sort.model';
import { SortDirection } from '../models/shared/base-request.model';
import { filePreviewCache } from '../config/file-preview.config';

interface CachedThumbnail {
  expiresAt: number;
  request: Observable<Blob>;
}
@Injectable({
  providedIn: 'root'
})
export class FilesService {
  private readonly apiUrl = `${environment.apiUrl}/api/files`;
  private readonly thumbnailCache = new Map<string, CachedThumbnail>();
  constructor(private http: HttpClient) { }
  getFiles(pageNumber: number = 1, search: string = '', pageSize: number = 10, sortBy: FileSortBy = 'DateCreated', sortDirection: SortDirection = 'Descending'): Observable<PaginatedList<FileModel>> {
    const request: GetFilesRequest = { search, pageNumber, pageSize, sortBy, sortDirection };
    const params = new HttpParams()
      .set('search', request.search)
      .set('pageNumber', request.pageNumber)
      .set('pageSize', request.pageSize)
      .set('sortBy', request.sortBy)
      .set('sortDirection', request.sortDirection);
    return this.http.get<PaginatedList<FileModel>>(this.apiUrl, { params });
  }
  uploadFile(file: File, description: string = '', orderId?: string | null, forMessage = false): Observable<FileModel> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append('description', description);
    if (forMessage)
      formData.append('forMessage', 'true');
    if (orderId) {
      formData.append('orderId', orderId);
    }
    return this.http.post<FileModel>(this.apiUrl, formData);
  }
  uploadFileForOrder(orderId: string, file: File, description: string = ''): Observable<FileModel> {
    return this.uploadFile(file, description, orderId);
  }
  uploadMessageAttachment(orderId: string, file: File): Observable<FileModel> {
    return this.uploadFile(file, '', orderId, true);
  }
  requestDownloadTicket(fileId: string): Observable<{
    ticket: string;
  }> {
    return this.http.post<{
      ticket: string;
    }>(`${this.apiUrl}/${fileId}/download-ticket`, {});
  }
  previewImage(fileId: string): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/${fileId}/preview`, { responseType: 'blob' });
  }
  thumbnailImage(fileId: string): Observable<Blob> {
    // A cached image must never be reused by a different authenticated browser session.
    const cacheKey = `${localStorage.getItem('access_token')}|${fileId}`;
    const cachedThumbnail = this.thumbnailCache.get(cacheKey);
    if (cachedThumbnail && cachedThumbnail.expiresAt > Date.now())
      return cachedThumbnail.request;

    const request: Observable<Blob> = this.http.get(`${this.apiUrl}/${fileId}/thumbnail`, { responseType: 'blob' }).pipe(
      catchError(error => {
        if (this.thumbnailCache.get(cacheKey)?.request === request)
          this.thumbnailCache.delete(cacheKey);
        return throwError(() => error);
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );
    this.thumbnailCache.set(cacheKey, { expiresAt: Date.now() + filePreviewCache.lifetimeMs, request });
    this.trimThumbnailCache();
    return request;
  }

  private trimThumbnailCache(): void {
    while (this.thumbnailCache.size > filePreviewCache.maximumEntries)
      this.thumbnailCache.delete(this.thumbnailCache.keys().next().value!);
  }
  startBrowserDownload(ticket: string): void {
    const frameName = `file-download-${crypto.randomUUID()}`;
    const frame = document.createElement('iframe');
    frame.name = frameName;
    frame.hidden = true;
    const form = document.createElement('form');
    form.method = 'post';
    form.action = `${this.apiUrl}/download`;
    form.target = frameName;
    form.hidden = true;
    const ticketInput = document.createElement('input');
    ticketInput.type = 'hidden';
    ticketInput.name = 'ticket';
    ticketInput.value = ticket;
    form.appendChild(ticketInput);
    document.body.append(frame, form);
    form.submit();
    form.remove();
    window.setTimeout(() => frame.remove(), 60000);
  }
}
